import React, { useState, useEffect } from "react";
import { Plus, X, Save, AlertCircle, CheckCircle, Clock, Edit2, Download } from "lucide-react";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import { useDispatch, useSelector } from "react-redux";
import {
  createObjectives,
  updateObjective,
  fetchObjectivesByTeacher,
  clearError,
} from "../../../redux/annualPersonalObjectiveSlice";

// Modal for adding/editing objectives
const ObjectiveModal = ({ isOpen, onClose, onSubmit, objective = null }) => {
  const [performanceYear, setPerformanceYear] = useState(
    objective?.performanceYear || new Date().getFullYear().toString()
  );
  const [objectivesList, setObjectivesList] = useState(
    objective
      ? [
          {
            id: 1,
            objective: objective.objective || "",
            minimum: objective.minimum || "",
            onTarget: objective.onTarget || "",
            exceptional: objective.exceptional || "",
          },
        ]
      : [
          {
            id: 1,
            objective: "",
            minimum: "",
            onTarget: "",
            exceptional: "",
          },
        ]
  );

  React.useEffect(() => {
    if (objective) {
      setPerformanceYear(objective.performanceYear || objective.year || new Date().getFullYear().toString());
      setObjectivesList([
        {
          id: 1,
          objective: objective.objective || "",
          minimum: objective.minimum || objective.minExpectation || "",
          onTarget: objective.onTarget || "",
          exceptional: objective.exceptional || "",
        },
      ]);
    } else {
      setPerformanceYear(new Date().getFullYear().toString());
      setObjectivesList([
        {
          id: 1,
          objective: "",
          minimum: "",
          onTarget: "",
          exceptional: "",
        },
      ]);
    }
  }, [objective, isOpen]);

  const addObjectiveRow = () => {
    setObjectivesList([
      ...objectivesList,
      {
        id: Date.now(),
        objective: "",
        minimum: "",
        onTarget: "",
        exceptional: "",
      },
    ]);
  };

  const removeObjectiveRow = (id) => {
    if (objectivesList.length > 1) {
      setObjectivesList(objectivesList.filter((obj) => obj.id !== id));
    } else {
      toast.error("At least one objective is required");
    }
  };

  const updateObjective = (id, field, value) => {
    setObjectivesList(
      objectivesList.map((obj) =>
        obj.id === id ? { ...obj, [field]: value } : obj
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that at least one objective has a description
    const validObjectives = objectivesList.filter(
      (obj) => obj.objective.trim() !== ""
    );

    if (validObjectives.length === 0) {
      toast.error("Please enter at least one objective");
      return;
    }

    // If editing single objective, submit as before
    if (objective) {
      const formData = {
        ...validObjectives[0],
        performanceYear,
      };
      onSubmit(formData);
    } else {
      // Submit all objectives for the year
      const objectivesData = validObjectives.map((obj) => ({
        ...obj,
        performanceYear,
      }));
      onSubmit(objectivesData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-11/12 max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
        >
          <X size={24} />
        </button>
        <div className="p-6">
          <h2 className="text-xl font-bold text-[#241763] mb-4">
            {objective ? "Edit Objective" : "Add Objectives for Year"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Performance Year
              </label>
              <input
                type="text"
                value={performanceYear}
                onChange={(e) => setPerformanceYear(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763] disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="e.g., 2024"
                disabled={!!objective}
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-700">
                  Objectives ({objectivesList.length})
                </h3>
                {!objective && (
                  <button
                    type="button"
                    onClick={addObjectiveRow}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#241763] text-white rounded hover:bg-[#1a1247] text-sm"
                  >
                    <Plus size={16} />
                    Add Another Objective
                  </button>
                )}
              </div>

              {objectivesList.map((obj, index) => (
                <div
                  key={obj.id}
                  className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-700">
                      Objective {index + 1}
                    </h4>
                    {!objective && objectivesList.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeObjectiveRow(obj.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove this objective"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Objective <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={obj.objective}
                        onChange={(e) =>
                          updateObjective(obj.id, "objective", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                        rows="3"
                        placeholder="Enter the objective description..."
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Minimum
                        </label>
                        <textarea
                          value={obj.minimum}
                          onChange={(e) =>
                            updateObjective(obj.id, "minimum", e.target.value)
                          }
                          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                          rows="2"
                          placeholder="Minimum expectation..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          On Target
                        </label>
                        <textarea
                          value={obj.onTarget}
                          onChange={(e) =>
                            updateObjective(obj.id, "onTarget", e.target.value)
                          }
                          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                          rows="2"
                          placeholder="On target expectation..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Exceptional
                        </label>
                        <textarea
                          value={obj.exceptional}
                          onChange={(e) =>
                            updateObjective(
                              obj.id,
                              "exceptional",
                              e.target.value
                            )
                          }
                          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                          rows="2"
                          placeholder="Exceptional expectation..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#241763] text-white rounded hover:bg-[#1a1247] flex items-center gap-2"
              >
                <Save size={16} />
                {objective
                  ? "Update Objective"
                  : `Save ${objectivesList.filter((o) => o.objective.trim()).length} Objective(s)`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Modal for viewing objective details
const ObjectiveViewModal = ({ isOpen, onClose, objective, onEdit, onSend }) => {
  if (!isOpen || !objective) return null;

  const canEdit = objective.status !== "accepted" && objective.status !== "completed";
  const canSend = objective.status === "pending" || objective.status === "rejected";
  const isRejected = objective.status === "rejected";

  const getStatusIcon = () => {
    switch (objective.status) {
      case "accepted":
      case "completed":
        return <CheckCircle size={16} className="text-green-500" />;
      case "rejected":
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-yellow-500" />;
    }
  };

  const getStatusColor = () => {
    switch (objective.status) {
      case "accepted":
      case "completed":
        return "text-green-500";
      case "rejected":
        return "text-red-500";
      default:
        return "text-yellow-500";
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-11/12 max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
        >
          <X size={24} />
        </button>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            {getStatusIcon()}
            <h2 className="text-xl font-bold text-[#241763]">
              Objective Details
            </h2>
            <span
              className={`ml-auto px-3 py-1 rounded text-xs font-medium capitalize ${getStatusColor()}`}
            >
              {objective.status}
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Performance Year
              </label>
              <p className="text-gray-900">{objective.year || objective.performanceYear}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Objective
              </label>
              <p className="text-gray-900">{objective.objective}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-blue-700 mb-2">
                  Minimum
                </label>
                <p className="text-sm text-gray-700">
                  {objective.minExpectation || objective.minimum || "Not specified"}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-green-700 mb-2">
                  On Target
                </label>
                <p className="text-sm text-gray-700">
                  {objective.onTarget || "Not specified"}
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-purple-700 mb-2">
                  Exceptional
                </label>
                <p className="text-sm text-gray-700">
                  {objective.exceptional || "Not specified"}
                </p>
              </div>
            </div>

            {objective.teacherNotes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Teacher Notes
                </label>
                <p className="text-sm text-gray-700">{objective.teacherNotes}</p>
              </div>
            )}

            {(objective.createdAt || objective.createdDate) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created Date
                </label>
                <p className="text-gray-900">
                  {new Date(objective.createdAt || objective.createdDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
            {canEdit && (
              <button
                onClick={() => {
                  onEdit(objective);
                  onClose();
                }}
                className={`px-4 py-2 text-white rounded ${
                  isRejected
                    ? "bg-orange-600 hover:bg-orange-700"
                    : "bg-[#241763] hover:bg-[#1a1247]"
                }`}
              >
                {isRejected ? "Recreate Objective" : "Edit"}
              </button>
            )}
            {canSend && !isRejected && (
              <button
                onClick={() => {
                  onSend(objective.id);
                  onClose();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Send to Teacher
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PDPObjectives = ({ teacherEmail }) => {
  const dispatch = useDispatch();
  const { objectives, loading, error } = useSelector(
    (state) => state.annualPersonalObjective
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isYearObjectivesModalOpen, setIsYearObjectivesModalOpen] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [editingObjective, setEditingObjective] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  // Fetch objectives when component mounts or teacherEmail changes
  useEffect(() => {
    if (teacherEmail) {
      dispatch(fetchObjectivesByTeacher(teacherEmail));
    }
  }, [dispatch, teacherEmail]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "accepted":
      case "completed":
        return <CheckCircle size={16} className="text-green-500" />;
      case "rejected":
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-yellow-500" />;
    }
  };

  const handleAddObjective = async (formData) => {
    try {
      // Check if formData is an array (multiple objectives) or single object
      if (Array.isArray(formData)) {
        // Multiple objectives for the year
        const objectivesPayload = {
          objectives: formData.map((obj) => ({
            teacherEmail: teacherEmail,
            year: obj.performanceYear,
            objective: obj.objective,
            minExpectation: obj.minimum || "",
            onTarget: obj.onTarget || "",
            exceptional: obj.exceptional || "",
          })),
        };
        await dispatch(createObjectives(objectivesPayload)).unwrap();
        setIsAddModalOpen(false);
        toast.success(`${formData.length} objectives added successfully`);
        // Refresh objectives
        dispatch(fetchObjectivesByTeacher(teacherEmail));
      } else {
        // Single objective (shouldn't happen in add mode, but handle it)
        const objectivesPayload = {
          objectives: [
            {
              teacherEmail: teacherEmail,
              year: formData.performanceYear,
              objective: formData.objective,
              minExpectation: formData.minimum || "",
              onTarget: formData.onTarget || "",
              exceptional: formData.exceptional || "",
            },
          ],
        };
        await dispatch(createObjectives(objectivesPayload)).unwrap();
        setIsAddModalOpen(false);
        toast.success("Objective added successfully");
        // Refresh objectives
        dispatch(fetchObjectivesByTeacher(teacherEmail));
      }
    } catch (err) {
      toast.error(err || "Failed to add objectives");
    }
  };

  const handleEditObjective = (objective) => {
    setEditingObjective(objective);
    setIsEditModalOpen(true);
  };

  const handleUpdateObjective = async (formData) => {
    try {
      const originalObjective = objectives.find((obj) => obj.id === editingObjective.id);
      
      // Prepare update data
      const updateData = {
        objective: formData.objective,
        minExpectation: formData.minimum || "",
        onTarget: formData.onTarget || "",
        exceptional: formData.exceptional || "",
        year: formData.performanceYear,
      };

      // If the objective was rejected, we recreate it (status will be reset to pending on backend)
      if (originalObjective.status === "rejected") {
        updateData.status = "pending";
        updateData.teacherNotes = "";
      }

      await dispatch(
        updateObjective({
          objectiveId: editingObjective.id,
          updateData,
        })
      ).unwrap();

      setEditingObjective(null);
      setIsEditModalOpen(false);
      
      if (originalObjective.status === "rejected") {
        toast.success("Objective recreated and sent for review");
      } else {
        toast.success("Objective updated successfully");
      }
      
      // Refresh objectives
      dispatch(fetchObjectivesByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to update objective");
    }
  };

  const handleViewObjective = (objective) => {
    setSelectedObjective(objective);
    setIsViewModalOpen(true);
  };

  const handleSendObjective = async (id) => {
    try {
      // Update objective status to pending
      await dispatch(
        updateObjective({
          objectiveId: id,
          updateData: { status: "pending" },
        })
      ).unwrap();
      toast.success("Objective sent to teacher for review");
      // Refresh objectives
      dispatch(fetchObjectivesByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to send objective");
    }
  };

  const handleDeleteObjective = async (id) => {
    const objective = objectives.find((obj) => obj.id === id);
    if (objective.status === "accepted" || objective.status === "completed") {
      toast.error("Cannot delete accepted or completed objectives");
      return;
    }
    if (window.confirm("Are you sure you want to delete this objective?")) {
      // Note: Delete endpoint not provided in API, so we'll just show a message
      // If delete endpoint exists, add it to the slice and call it here
      toast.info("Delete functionality will be available once the delete endpoint is implemented");
    }
  };

  // Group objectives by year
  const objectivesByYear = (objectives || []).reduce((acc, obj) => {
    const year = obj.year || obj.performanceYear || "Unknown";
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(obj);
    return acc;
  }, {});

  const years = Object.keys(objectivesByYear).sort((a, b) => b.localeCompare(a));

  const handleViewYearObjectives = (year) => {
    setSelectedYear(year);
    setIsYearObjectivesModalOpen(true);
  };

  const handleDownloadYearObjectivesPdf = () => {
    if (!selectedYear || !objectivesByYear[selectedYear]?.length) {
      toast.error("No objectives to download");
      return;
    }
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let y = 20;
      const lineHeight = 7;
      const maxWidth = pageWidth - margin * 2;

      const wrapText = (text, x, yPos, maxW) => {
        const lines = doc.splitTextToSize(text || "—", maxW);
        doc.text(lines, x, yPos);
        return yPos + lines.length * lineHeight;
      };

      // Title
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Annual Personal Objectives", margin, y);
      y += lineHeight + 4;

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Year: ${selectedYear}`, margin, y);
      if (teacherEmail) {
        doc.text(`Teacher: ${teacherEmail}`, margin, y + lineHeight);
        y += lineHeight;
      }
      y += lineHeight + 8;

      const yearObjectives = objectivesByYear[selectedYear];
      yearObjectives.forEach((obj, index) => {
        if (y > 260) {
          doc.addPage();
          y = 20;
        }

        const normalizedObjective = {
          ...obj,
          minimum: obj.minExpectation ?? obj.minimum,
          createdDate: obj.createdAt || obj.createdDate,
        };

        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(`Objective ${index + 1}`, margin, y);
        y += lineHeight;

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        const statusLabel = (obj.status || "").charAt(0).toUpperCase() + (obj.status || "").slice(1);
        doc.text(`Status: ${statusLabel}`, margin, y);
        if (normalizedObjective.createdDate) {
          doc.text(
            `Date: ${new Date(normalizedObjective.createdDate).toLocaleDateString()}`,
            margin + 50,
            y
          );
        }
        y += lineHeight + 2;

        y = wrapText(obj.objective || "", margin, y, maxWidth) + 4;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("Minimum:", margin, y);
        const minLines = doc.splitTextToSize(String(normalizedObjective.minimum || "Not specified"), maxWidth - 28);
        doc.setFont("helvetica", "normal");
        doc.text(minLines, margin + 28, y);
        y += minLines.length * lineHeight + 2;

        doc.setFont("helvetica", "bold");
        doc.text("On Target:", margin, y);
        const onTargetLines = doc.splitTextToSize(String(obj.onTarget || "Not specified"), maxWidth - 28);
        doc.setFont("helvetica", "normal");
        doc.text(onTargetLines, margin + 28, y);
        y += onTargetLines.length * lineHeight + 2;

        doc.setFont("helvetica", "bold");
        doc.text("Exceptional:", margin, y);
        const excLines = doc.splitTextToSize(String(obj.exceptional || "Not specified"), maxWidth - 28);
        doc.setFont("helvetica", "normal");
        doc.text(excLines, margin + 28, y);
        y += excLines.length * lineHeight + 4;

        if (obj.teacherNotes) {
          doc.setFont("helvetica", "normal");
          y = wrapText(`Teacher Notes: ${obj.teacherNotes}`, margin, y, maxWidth) + 4;
        }

        y += 6;
      });

      doc.save(`Annual-Personal-Objectives-${selectedYear}.pdf`);
      toast.success("PDF downloaded successfully");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to download PDF");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <h1 className="font-bold text-base text-blue-500">
          PDP ({objectives?.length || 0})
        </h1>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#241763] text-white p-1.5 rounded-full hover:bg-[#241763]/80 flex items-center justify-center"
          title="Add Objective"
          disabled={loading}
        >
          <Plus size={16} />
        </button>
      </div>
      {loading && (
        <div className="text-center py-4 text-gray-500">
          <p>Loading objectives...</p>
        </div>
      )}
      {!loading && objectives && objectives.length > 0 ? (
        <div className="space-y-1">
          {years.map((year) => {
            const yearObjectives = objectivesByYear[year];
            const pendingCount = yearObjectives.filter((o) => o.status === "pending").length;
            const acceptedCount = yearObjectives.filter((o) => o.status === "accepted" || o.status === "completed").length;
            const rejectedCount = yearObjectives.filter((o) => o.status === "rejected").length;
            
            return (
              <div
                key={year}
                className="border-b pb-2 flex justify-between items-center hover:bg-gray-50 rounded px-1"
              >
                <button
                  onClick={() => handleViewYearObjectives(year)}
                  className="text-blue-500 hover:text-blue-700 underline text-left flex-1 min-w-0"
                >
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-semibold">Year {year}</span>
                      <span className="text-xs text-gray-500">
                        ({yearObjectives.length} objectives)
                      </span>
                      {pendingCount > 0 && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                          {pendingCount} pending
                        </span>
                      )}
                      {acceptedCount > 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                          {acceptedCount} accepted
                        </span>
                      )}
                      {rejectedCount > 0 && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                          {rejectedCount} rejected
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      ) : !loading ? (
        <div className="flex flex-col items-center justify-center py-4 text-gray-500">
          <p>No objectives added</p>
          <p className="text-sm">Click the + button to add an objective</p>
        </div>
      ) : null}

      {/* Add Objective Modal */}
      <ObjectiveModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddObjective}
      />

      {/* Edit Objective Modal */}
      <ObjectiveModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingObjective(null);
        }}
        onSubmit={handleUpdateObjective}
        objective={editingObjective}
      />

      {/* View Objective Modal */}
      <ObjectiveViewModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedObjective(null);
        }}
        objective={selectedObjective}
        onEdit={handleEditObjective}
        onSend={handleSendObjective}
      />

      {/* Year Objectives Modal */}
      {isYearObjectivesModalOpen && selectedYear && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-5xl w-11/12 max-h-[90vh] overflow-y-auto relative">
            <div className="absolute top-4 right-4 flex items-center gap-2">
              <button
                onClick={handleDownloadYearObjectivesPdf}
                className="flex items-center gap-2 px-4 py-2 bg-[#241763] text-white rounded-lg hover:bg-[#1a1247] text-sm font-medium"
                title="Download as PDF"
              >
                <Download size={18} />
                Download as PDF
              </button>
              <button
                onClick={() => {
                  setIsYearObjectivesModalOpen(false);
                  setSelectedYear(null);
                }}
                className="text-gray-600 hover:text-gray-900 p-1"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <h2 className="text-xl font-bold text-[#241763] mb-4 pr-40">
                Objectives for Year {selectedYear}
              </h2>
              <div className="space-y-4">
                {objectivesByYear[selectedYear]?.map((objective) => {
                  const normalizedObjective = {
                    ...objective,
                    performanceYear: objective.year || objective.performanceYear,
                    minimum: objective.minExpectation || objective.minimum,
                    createdDate: objective.createdAt || objective.createdDate,
                  };
                  
                  return (
                    <div
                      key={objective.id}
                      className={`border-2 rounded-lg p-4 ${
                        objective.status === "accepted"
                          ? "border-green-300 bg-green-50"
                          : objective.status === "rejected"
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getStatusIcon(objective.status)}
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                objective.status === "accepted"
                                  ? "bg-green-200 text-green-800"
                                  : objective.status === "rejected"
                                  ? "bg-red-200 text-red-800"
                                  : "bg-yellow-200 text-yellow-800"
                              }`}
                            >
                              {objective.status === "accepted"
                                ? "Accepted"
                                : objective.status === "rejected"
                                ? "Rejected"
                                : "Pending"}
                            </span>
                            {normalizedObjective.createdDate && (
                              <span className="text-xs text-gray-500">
                                {new Date(normalizedObjective.createdDate).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <h3 className="font-semibold text-gray-800 mb-2">
                            {objective.objective}
                          </h3>
                        </div>
                        <div className="flex gap-2">
                          {(objective.status !== "accepted" && objective.status !== "completed") && (
                            <button
                              onClick={() => {
                                setIsYearObjectivesModalOpen(false);
                                handleEditObjective(normalizedObjective);
                              }}
                              className="text-[#241763] hover:text-[#1a1247]"
                              title={objective.status === "rejected" ? "Recreate Objective" : "Edit"}
                            >
                              <Edit2 size={18} />
                            </button>
                          )}
                          {objective.status === "rejected" && (
                            <button
                              onClick={() => {
                                setIsYearObjectivesModalOpen(false);
                                handleEditObjective(normalizedObjective);
                              }}
                              className="px-3 py-1 bg-[#241763] text-white rounded hover:bg-[#1a1247] text-sm"
                              title="Recreate this rejected objective"
                            >
                              Recreate
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div className="bg-blue-50 p-3 rounded">
                          <p className="text-xs font-semibold text-blue-700 mb-1">
                            Minimum
                          </p>
                          <p className="text-sm text-gray-700">
                            {normalizedObjective.minimum || "Not specified"}
                          </p>
                        </div>
                        <div className="bg-green-50 p-3 rounded">
                          <p className="text-xs font-semibold text-green-700 mb-1">
                            On Target
                          </p>
                          <p className="text-sm text-gray-700">
                            {objective.onTarget || "Not specified"}
                          </p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded">
                          <p className="text-xs font-semibold text-purple-700 mb-1">
                            Exceptional
                          </p>
                          <p className="text-sm text-gray-700">
                            {objective.exceptional || "Not specified"}
                          </p>
                        </div>
                      </div>
                      {objective.teacherNotes && (
                        <div className="mt-3 p-3 bg-gray-100 rounded">
                          <p className="text-xs font-semibold text-gray-700 mb-1">
                            Teacher Notes:
                          </p>
                          <p className="text-sm text-gray-600">
                            {objective.teacherNotes}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDPObjectives;
